import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useRef, useLayoutEffect, useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Button, Tooltip } from 'react-daisyui';
import useAIResponseAction from './AIResponseAction.hook';
import AIResponsePreview from './components/AIResponsePreview/AIResponsePreview.component';
import type { tDisabledReasonInfo } from '../AutoRespondToggle/AutoRespondToggle.hook';

type tAIResponseActionProps = {
  tooltipMessage: string;
  autoRespondEnabled: boolean;
  autoRespondSaving: boolean;
  onAutoRespondToggle: () => void;
  autoRespondDisabledReason: tDisabledReasonInfo | null;
  onIntervene?: () => void;
  onGuide?: () => void;
};

export default function AIResponseAction(props: tAIResponseActionProps) {
  const {
    dropdownRef,
    portalRef,
    isOpen,
    isLoading,
    handleToggle,
    closeMenu,
    selectedChat,
  } = useAIResponseAction();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const menuWidth = 256; // w-64 — keep in sync with AIResponsePreview's card width
      const rect = triggerRef.current.getBoundingClientRect();
      const rawLeft = rect.left + rect.width / 2 - menuWidth / 2;
      setMenuPos({
        bottom: window.innerHeight - rect.top + 8,
        left: Math.max(8, Math.min(rawLeft, window.innerWidth - menuWidth - 8)),
      });
    }
  }, [isOpen]);

  if (!selectedChat) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <Tooltip message={isLoading ? 'Generando respuesta...' : props.tooltipMessage}>
        <Button
          color="ghost"
          onClick={handleToggle}
          size="xs"
          className="MessageBar__Button relative"
          disabled={isLoading}
          ref={triggerRef}
        >
          {props.autoRespondEnabled && (
            <span className="ToolActions__AIStatusDot" />
          )}
          <SparklesIcon
            className={classNames('MessageBar__Icon', {
              'animate-pulse': isLoading,
            })}
          />
        </Button>
      </Tooltip>

      {isOpen && createPortal(
        <div
          ref={portalRef}
          className="fixed z-[9999]"
          style={{ bottom: menuPos.bottom, left: menuPos.left }}
        >
          <AIResponsePreview
            autoRespondEnabled={props.autoRespondEnabled}
            autoRespondSaving={props.autoRespondSaving}
            onAutoRespondToggle={props.onAutoRespondToggle}
            disabledReason={props.autoRespondDisabledReason}
            // EXPERIMENT: close the popover when an action is chosen so the
            // user lands directly in the resulting state (composer/director).
            onIntervene={props.onIntervene && (() => { props.onIntervene?.(); closeMenu(); })}
            onGuide={props.onGuide && (() => { props.onGuide?.(); closeMenu(); })}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
